import { useTranslation } from 'react-i18next';
import {
  ArrowDownUp,
  CalendarClock,
  RotateCcw,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';
import type { RouteSettingSetter } from '../../../types/route-setting-board';
import { BOARD_SORTS, type BoardFilters, type BoardSort } from './board-utils';

interface BoardFilterBarProps {
  filters: BoardFilters;
  onChange: (filters: BoardFilters) => void;
  sort: BoardSort;
  onSortChange: (sort: BoardSort) => void;
  /** Widest age range in the data — the slider's endpoints. */
  bounds: [number, number];
  setters: RouteSettingSetter[];
  grades: string[];
  holdColors: Array<{ name: string; hex: string | null }>;
  onReset: () => void;
}

interface MultiSelectOption {
  value: string;
  label: string;
  /** Drawn as a dot before the label — used by the hold-colour filter. */
  swatch?: string | null;
}

interface MultiSelectProps {
  label: string;
  emptyLabel: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  testId: string;
}

/**
 * Every control on this bar is one small button: the board is the page, so the
 * toolbar gets a single line of it.
 */
const TRIGGER = 'h-8 max-w-[12rem] justify-between gap-1.5 px-2.5 text-xs font-normal';

/** A filter that is narrowing the board says so without being read. */
const ACTIVE = 'border-primary/60 bg-primary/5';

/**
 * Radix's viewport is `h-full`, so a max-height on the scroll area's root alone
 * leaves the viewport as tall as its content and the root simply clips it — the
 * list has to be capped on both for a long one to actually scroll.
 */
const SCROLL_LIST = 'max-h-56 [&>[data-radix-scroll-area-viewport]]:max-h-56';

/**
 * A checkbox list. No selection means "all", which keeps the default board
 * unfiltered without a separate "all" entry to keep in sync.
 */
function MultiSelect({
  label,
  emptyLabel,
  options,
  selected,
  onChange,
  testId,
}: MultiSelectProps) {
  const { t } = useTranslation();

  const toggle = (value: string) => {
    onChange(
      selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]
    );
  };

  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs">{label}</Label>
        {/* Picking nothing is what shows everything, so an untouched group says
            so where the count would otherwise be. */}
        {selected.length === 0 ? (
          <span className="text-xs text-muted-foreground">{emptyLabel}</span>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-1.5 text-xs font-normal text-muted-foreground"
            onClick={() => onChange([])}
          >
            <X className="mr-1 size-3" aria-hidden />
            {selected.length}
          </Button>
        )}
      </div>

      {options.length === 0 ? (
        <p className="rounded-md border border-dashed px-2 py-3 text-xs text-muted-foreground">
          {t('gymAdmin.routeSettingBoard.filters.empty')}
        </p>
      ) : (
        <ScrollArea className={cn('rounded-md border', SCROLL_LIST)}>
          <div className="space-y-1 p-2">
            {options.map((option) => (
              <label
                key={option.value}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent"
              >
                <Checkbox
                  checked={selected.includes(option.value)}
                  onCheckedChange={() => toggle(option.value)}
                />
                {option.swatch && (
                  <span
                    className="size-3 shrink-0 rounded-full border border-border"
                    style={{ backgroundColor: option.swatch }}
                  />
                )}
                <span className="truncate">{option.label}</span>
              </label>
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}

export function BoardFilterBar({
  filters,
  onChange,
  sort,
  onSortChange,
  bounds,
  setters,
  grades,
  holdColors,
  onReset,
}: BoardFilterBarProps) {
  const { t } = useTranslation();
  const [minBound, maxBound] = bounds;
  // A one-day-wide board would give the slider a zero-length track; nudge the
  // top end so both thumbs stay grabbable.
  const sliderMax = maxBound > minBound ? maxBound : minBound + 1;

  const narrowedAge = filters.ageRange[0] > minBound || filters.ageRange[1] < maxBound;
  // What the dialog holds, counted on its button: the toolbar has no room to
  // show which of them are on, but it can say how many.
  const advancedCount =
    (narrowedAge ? 1 : 0) +
    (filters.setterIds.length > 0 ? 1 : 0) +
    (filters.grades.length > 0 ? 1 : 0) +
    (filters.holdColors.length > 0 ? 1 : 0);

  // The reset only appears once there is something to reset, so an untouched
  // board spends no width on it.
  const filtering = advancedCount > 0 || filters.name.trim().length > 0;

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="board-filter-bar">
      {/* Name and order stay on the toolbar: one is typed at constantly and the
          other is read at a glance, so neither is worth a click to reach. */}
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          value={filters.name}
          onChange={(event) => onChange({ ...filters, name: event.target.value })}
          placeholder={t('gymAdmin.routeSettingBoard.filters.namePlaceholder')}
          aria-label={t('gymAdmin.routeSettingBoard.filters.name')}
          data-testid="board-name-filter"
          className={cn('h-8 w-44 pl-7 pr-7 text-xs', filters.name.length > 0 && ACTIVE)}
        />
        {filters.name.length > 0 && (
          <button
            type="button"
            onClick={() => onChange({ ...filters, name: '' })}
            aria-label={t('gymAdmin.routeSettingBoard.filters.clearName')}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-muted-foreground hover:text-foreground"
          >
            <X className="size-3.5" aria-hidden />
          </button>
        )}
      </div>

      <Select value={sort} onValueChange={(next) => onSortChange(next as BoardSort)}>
        <SelectTrigger
          className={cn(TRIGGER, 'w-auto')}
          aria-label={t('gymAdmin.routeSettingBoard.sort.label')}
          data-testid="board-sort"
        >
          <ArrowDownUp className="size-3.5 shrink-0 opacity-70" aria-hidden />
          <SelectValue />
        </SelectTrigger>
        <SelectContent align="start">
          {BOARD_SORTS.map((option) => (
            <SelectItem key={option} value={option} className="text-xs">
              {t(`gymAdmin.routeSettingBoard.sort.${option}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Everything else is one click away: the board is the page, and these
          four are set once and left alone. */}
      <Dialog>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn('h-8 gap-1.5 px-2.5', advancedCount > 0 && ACTIVE)}
            aria-label={t('gymAdmin.routeSettingBoard.filters.title')}
            title={t('gymAdmin.routeSettingBoard.filters.title')}
            data-testid="board-filters-open"
          >
            <SlidersHorizontal className="size-3.5" aria-hidden />
            {advancedCount > 0 && (
              <span className="rounded-full bg-primary/15 px-1.5 text-xs font-semibold">
                {advancedCount}
              </span>
            )}
          </Button>
        </DialogTrigger>

        <DialogContent className="max-h-[85svh] gap-3 overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('gymAdmin.routeSettingBoard.filters.title')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs">
                {t('gymAdmin.routeSettingBoard.filters.age')}
              </Label>
              {maxBound === minBound ? (
                <p className="rounded-md border border-dashed px-2 py-3 text-xs text-muted-foreground">
                  {t('gymAdmin.routeSettingBoard.filters.allAges')}
                </p>
              ) : (
                <>
                  <Slider
                    min={minBound}
                    max={sliderMax}
                    step={1}
                    value={filters.ageRange}
                    onValueChange={([min, max]) => onChange({ ...filters, ageRange: [min, max] })}
                    aria-label={t('gymAdmin.routeSettingBoard.filters.age')}
                  />
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <CalendarClock className="size-3.5 shrink-0 opacity-70" aria-hidden />
                    {t('gymAdmin.routeSettingBoard.filters.ageRange', {
                      from: filters.ageRange[0],
                      to: filters.ageRange[1],
                    })}
                  </p>
                </>
              )}
            </div>

            <MultiSelect
              label={t('gymAdmin.routeSettingBoard.filters.setters')}
              emptyLabel={t('gymAdmin.routeSettingBoard.filters.allSetters')}
              options={setters.map((setter) => ({
                value: setter.id,
                label: setter.name,
              }))}
              testId="board-filter-setters"
              selected={filters.setterIds}
              onChange={(setterIds) => onChange({ ...filters, setterIds })}
            />

            <MultiSelect
              label={t('gymAdmin.routeSettingBoard.filters.grades')}
              emptyLabel={t('gymAdmin.routeSettingBoard.filters.allGrades')}
              options={grades.map((grade) => ({ value: grade, label: grade }))}
              testId="board-filter-grades"
              selected={filters.grades}
              onChange={(selected) => onChange({ ...filters, grades: selected })}
            />

            <MultiSelect
              label={t('gymAdmin.routeSettingBoard.filters.holdColors')}
              emptyLabel={t('gymAdmin.routeSettingBoard.filters.allHoldColors')}
              options={holdColors.map((color) => ({
                value: color.name,
                label: color.name,
                swatch: color.hex,
              }))}
              testId="board-filter-colors"
              selected={filters.holdColors}
              onChange={(selected) => onChange({ ...filters, holdColors: selected })}
            />
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="ghost" size="sm" onClick={onReset} disabled={!filtering}>
              <RotateCcw className="mr-1.5 size-3.5" aria-hidden />
              {t('gymAdmin.routeSettingBoard.filters.reset')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {filtering && (
        <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={onReset}>
          <RotateCcw className="mr-1.5 size-3.5" aria-hidden />
          {t('gymAdmin.routeSettingBoard.filters.reset')}
        </Button>
      )}
    </div>
  );
}
